import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useQueryClient } from 'react-query';
import { PageId, useWorkflowDataQuery, WorkflowIdEnum } from './api/useWorkflowDataQuery';
import { PAGE_MAP } from './Workflow.meta';
import { useResetModuleWrapperStyles } from '../_hooks/useResetModuleWrapperStyles';
import { useWorkflowPreviousMutation } from './api/useWorkflowPreviousMutation';
import { useWorkflowNextMutation, WorkflowDirection } from './api/useWorkflowNextMutation';
import { WorkflowStepper } from './components/WorkflowStepper/WorkflowStepper';
import { WorkflowLoading } from './Workflow.loading';
import { ScrollHereOnce } from '../CommonComponents/ScrollHereOnce';
import { WorkflowStart } from './components/WorkflowStart/WorkflowStart';
import { useBootstrapConsumerArea } from '../ConsumerArea/hooks/useBootstrapConsumerArea';
import { navigateToConsumerLandingPage } from '../ConsumerArea/utils/navigateToConsumerLandingPage';
import * as ConsumerTopNavActions from '../TopNav/Consumer/TopNavActions';
import { WorkflowError } from './Workflow.error';
import { MESSAGES } from '../CommonConstants';
import { toast } from '../CommonComponents/OPL/Toast/Toast';
import { queryKeys } from './api/queryKeys';
import { isConsumerArea, selectPortalName } from '../CommonSelectors/CommonAreaSelector';

interface WorkflowRouteProps {
  workflowId: string;
  stepId: string;
  pageId: PageId;
  clientId: string;
  pivotId: string;
  institutionId: string;
}

interface Props extends WorkflowRouteProps {
  componentDictionaryMap: Record<string, { component: React.ComponentType<any> }>;
}

export const Workflow: React.FC<Props> = ({
  clientId,
  stepId,
  pageId,
  workflowId: workflowIdFromRoute,
  componentDictionaryMap,
  ...rest
}) => {
  useResetModuleWrapperStyles();

  const workflowId = Number(workflowIdFromRoute) as WorkflowIdEnum;

  if (Number.isNaN(workflowId)) {
    throw new Error(`Invalid workflowId: ${workflowIdFromRoute}`);
  }

  const { isLoading, isError, refetch, data } = useWorkflowDataQuery({
    clientId,
    pageId,
    workflowId,
  });

  const { mutateAsync: workflowPrevious } = useWorkflowPreviousMutation({ clientId });
  const { mutateAsync: workflowNext } = useWorkflowNextMutation({ clientId });

  const {
    mutateAsync: bootstrapConsumerArea,
    isLoading: isBootstrappingConsumerArea,
  } = useBootstrapConsumerArea();

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const portalName = useSelector(selectPortalName);

  const isConsumer = useSelector(isConsumerArea);

  useEffectOnce(() => {
    document.title = `Workflow | ${portalName}`;

    return () => {
      queryClient.removeQueries(queryKeys.workflowAll());
    };
  });

  const onWorkflowDirection = async (direction: WorkflowDirection) => {
    const workflowDirection =
      direction === WorkflowDirection.Next ? workflowNext : workflowPrevious;

    try {
      const { Route } = await workflowDirection({ clientId, stepId, workflowId });

      if (Route.Name === 'Goal' && Route.Params.workflowId === 2 && isConsumer) {
        await bootstrapConsumerArea({
          clientId,
          completedWorkflow: true,
        });
      }

      if (Route.Name === 'Workflow') {
        ADV.util.Router.go2('Workflow', Route.Params);
      } else if (Route.Name === 'ConsumerWorkflowEnd') {
        const appVariables = await bootstrapConsumerArea({
          clientId,
          completedWorkflow: true,
        });

        dispatch(ConsumerTopNavActions.refresh_navigation());

        const { componentName, consumerAccessWorkflow } = appVariables;

        if (consumerAccessWorkflow === -1) {
          ADV.util.Router.go2(Route.Params.pageId || componentName, Route.Params);
        } else {
          navigateToConsumerLandingPage();
        }
      } else {
        ADV.util.Router.go2(Route.Name, Route.Params);
      }
    } catch (error) {
      console.error(error);

      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  if (pageId === PageId.Start) {
    return <WorkflowStart clientId={clientId} workflowId={workflowId} />;
  }

  if (isLoading || isBootstrappingConsumerArea) {
    return <WorkflowLoading />;
  }

  if (isError) {
    return <WorkflowError onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  const { CurStep } = data;

  const Component = componentDictionaryMap[PAGE_MAP[pageId]].component;

  return (
    <>
      <ScrollHereOnce key={pageId} options={false} />

      <WorkflowStepper clientId={clientId} workflowId={workflowId} pageId={pageId} />

      <Component
        workflowProps={{
          isFirstStep: CurStep <= 1,
          stepId,
          pageId,
          workflowId,
          onPrevious: () => onWorkflowDirection(WorkflowDirection.Previous),
          onNext: () => onWorkflowDirection(WorkflowDirection.Next),
        }}
        clientId={clientId}
        {...rest}
      />
    </>
  );
};
